import {
  Activity,
  Building2,
  Calendar,
  Database,
  Plane,
  Tag,
  UserRoundCog,
} from "lucide-react"

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  createDataTableColumnHelper,
  type DataTableFilterDef,
} from "@atlant/ui"

import { FleetStatusBadge } from "./fleet-status-badge.tsx"
import type { FleetRow } from "./fleet-table-types.ts"
import { FleetRowActions } from "./table-fleet-row-action.tsx"

const helper = createDataTableColumnHelper<FleetRow>()

const names = (list: { name: string }[] | null) => list?.map((x) => x.name) ?? []

const initials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase()

const formatCurrency = (value?: number) =>
  value != null ? `€${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}` : "—"

export const fleetColumns = helper.columns([
  helper.accessor("tail", {
    header: "Tail",
    size: 110,
    enableHiding: false,
    cell: (info) => <span className="font-medium">{info.getValue()}</span>,
  }),
  helper.accessor((row) => [row.manufacturer, row.model].filter(Boolean).join(" "), {
    id: "model",
    header: "Model",
    size: 220,
    filterFn: "anyOf",
  }),
  helper.accessor("yom", {
    id: "year",
    header: "Year",
    size: 90,
    filterFn: "anyOf",
  }),
  helper.accessor("category", {
    header: "Category",
    size: 140,
    filterFn: "anyOf",
  }),
  helper.accessor("source", {
    header: "Source",
    size: 110,
    filterFn: "anyOf",
  }),
  helper.accessor((row) => names(row.owners).join(", "), {
    id: "owner",
    header: "Owner",
    size: 180,
    getUniqueValues: (row) => names(row.owners),
    filterFn: "anyOf",
    cell: (info) => info.getValue() || "—",
  }),
  helper.accessor((row) => names(row.keyAccountManagers).join(", "), {
    id: "kam",
    header: "KAM",
    size: 220,
    getUniqueValues: (row) => names(row.keyAccountManagers),
    filterFn: "anyOf",
    cell: ({ row }) => {
      const kams = row.original.keyAccountManagers ?? []
      if (kams.length === 0) return "—"
      return (
        <div className="flex items-center gap-1.5">
          <div className="flex shrink-0 -space-x-2">
            {kams.map((k) => (
              <Avatar key={k.id} className="border-background size-7 border-2">
                <AvatarImage src={k.image ?? undefined} alt={k.name} />
                <AvatarFallback className="bg-avatar text-xs font-medium text-white">
                  {initials(k.name)}
                </AvatarFallback>
              </Avatar>
            ))}
          </div>
          <span className="truncate">{kams.map((k) => k.name).join(", ")}</span>
        </div>
      )
    },
  }),
  helper.accessor("lastMonth", {
    header: "Last month",
    size: 150,
    sortUndefined: "last",
    meta: { align: "end" },
    cell: (info) => formatCurrency(info.getValue()),
  }),
  helper.accessor("ytdHours", {
    header: "YTD Hours",
    size: 140,
    sortUndefined: "last",
    meta: { align: "end" },
    cell: (info) => info.getValue()?.toFixed(1) ?? "—",
  }),
  helper.accessor("status", {
    header: "Status",
    size: 150,
    minSize: 120,
    filterFn: "anyOf",
    cell: (info) => {
      const status = info.getValue()
      return status ? <FleetStatusBadge status={status} /> : "—"
    },
  }),
  helper.display({
    id: "actions",
    size: 56,
    enableHiding: false,
    enableResizing: false,
    cell: ({ row }) => (
      <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
        <FleetRowActions
          source={row.original.source}
          aircraftId={row.original.id}
          currentKamIds={row.original.keyAccountManagers.map((k) => k.id)}
          currentOwnerId={row.original.owners?.[0]?.id}
        />
      </div>
    ),
  }),
])

export const fleetFilters: DataTableFilterDef[] = [
  { columnId: "model", icon: Plane },
  { columnId: "year", icon: Calendar },
  { columnId: "category", icon: Tag },
  { columnId: "owner", icon: Building2 },
  { columnId: "kam", icon: UserRoundCog },
  { columnId: "status", icon: Activity },
  { columnId: "source", icon: Database },
]
